import React from "react";

import Select from "../../../../components/Select";
import NumberInput from "../../../../components/NumberInput";

// Historian defaults editor shared by ignition-gateways.js and
// opc-connections.js (and the interval/deadband math by mqtt-brokers.js).
// Mode/deadband values mirror tag.RecordingMode / tag.DeadbandMode on the
// backend — keep this the single source of truth so the UI clamp can't
// drift from the backend's historian_policy_defaults floor.
export const RECORD_MODE_OPTIONS = [
  { value: "0", label: "On change" },
  { value: "1", label: "Interval" },
  { value: "2", label: "On change + interval" },
];
export const DEADBAND_MODE_OPTIONS = [
  { value: "0", label: "None" },
  { value: "1", label: "Absolute" },
  { value: "2", label: "Percent" },
];
export const INTERVAL_UNIT_OPTIONS = [
  { value: "ms", label: "ms" },
  { value: "s", label: "sec" },
  { value: "m", label: "min" },
  { value: "h", label: "hour" },
];
export const INTERVAL_UNIT_MS = { ms: 1, s: 1000, m: 60000, h: 3600000 };
export const MIN_HISTORIAN_INTERVAL_MS = 500;
export const intervalToMs = (value, unit) => (parseInt(value, 10) || 0) * (INTERVAL_UNIT_MS[unit] || 1000);
export const historianIntervalToMs = (value, unit) => Math.max(intervalToMs(value, unit), MIN_HISTORIAN_INTERVAL_MS);
export const isIntervalHistorianMode = (mode) => mode === "1" || mode === "2";
export const historianIntervalInputMin = (unit) => (unit === "ms" ? MIN_HISTORIAN_INTERVAL_MS : 1);
// msToInterval picks the largest clean unit for display when editing. A stored
// 0 (on-change mode) maps to an empty value so an unrelated edit round-trips
// 0→0 instead of drifting to a synthesized default.
export const msToInterval = (ms) => {
  if (ms && ms % 3600000 === 0) return { value: String(ms / 3600000), unit: "h" };
  if (ms && ms % 60000 === 0) return { value: String(ms / 60000), unit: "m" };
  if (ms && ms % 1000 === 0) return { value: String(ms / 1000), unit: "s" };
  return ms ? { value: String(ms), unit: "ms" } : { value: "", unit: "s" };
};

// Default recording template: on-change with a 0.5% deadband — floods up to
// ~2Hz/tag on jittery analogs without swamping storage.
export const DEFAULT_HISTORY_TEMPLATE = {
  historianMode: "0",
  historianIntervalValue: "5",
  historianIntervalUnit: "s",
  deadbandMode: "2",
  deadbandValue: "0.5",
};

export const buildHistorianTemplatePayload = (form) => ({
  historian_mode: parseInt(form.historianMode, 10) || 0,
  historian_interval_ms: isIntervalHistorianMode(form.historianMode)
    ? historianIntervalToMs(form.historianIntervalValue, form.historianIntervalUnit)
    : 0,
  deadband_mode: parseInt(form.deadbandMode, 10) || 0,
  deadband_value: parseFloat(form.deadbandValue) || 0,
});

// Derives edit-form fields from a saved gateway/server record's historian_*
// fields, falling back to DEFAULT_HISTORY_TEMPLATE for anything absent.
export const templateFormFromRecord = (record) => {
  const historianMode = String(record?.historian_mode ?? 0);
  const interval = msToInterval(
    isIntervalHistorianMode(historianMode)
      ? Math.max(record?.historian_interval_ms || 0, MIN_HISTORIAN_INTERVAL_MS)
      : (record?.historian_interval_ms || 0)
  );
  return {
    historianMode,
    historianIntervalValue: interval.value || DEFAULT_HISTORY_TEMPLATE.historianIntervalValue,
    historianIntervalUnit: interval.unit,
    deadbandMode: String(record?.deadband_mode ?? DEFAULT_HISTORY_TEMPLATE.deadbandMode),
    deadbandValue: String(record?.deadband_value ?? DEFAULT_HISTORY_TEMPLATE.deadbandValue),
  };
};

// Minimal recording-defaults editor shared by the create and edit forms —
// seeds the historian_policy_defaults template applied to the connection's
// bound tags.
export const HistorianDefaultsFields = ({ form, set }) => (
  <div className="gwField">
    <div className="gwHistSection">Recording defaults</div>
    <div className="gwHistSectionBody">
      <div className="gwField">
        <label className="gwLabel">Record mode</label>
        <Select value={form.historianMode} onChange={set("historianMode")} options={RECORD_MODE_OPTIONS} />
      </div>
      {isIntervalHistorianMode(form.historianMode) && (
        <div style={{ display: "flex", gap: 12 }}>
          <div className="gwField" style={{ flex: 1 }}>
            <label className="gwLabel">Interval</label>
            <NumberInput
              min={historianIntervalInputMin(form.historianIntervalUnit)}
              step={1}
              value={form.historianIntervalValue}
              onChange={set("historianIntervalValue")}
              placeholder="5"
            />
          </div>
          <div className="gwField" style={{ flexShrink: 0 }}>
            <label className="gwLabel">Unit</label>
            <Select value={form.historianIntervalUnit} onChange={set("historianIntervalUnit")} options={INTERVAL_UNIT_OPTIONS} />
          </div>
        </div>
      )}
      {(form.historianMode === "0" || form.historianMode === "2") && (
        <div className="gwFieldRow">
          <div className="gwField">
            <label className="gwLabel">Deadband</label>
            <Select value={form.deadbandMode} onChange={set("deadbandMode")} options={DEADBAND_MODE_OPTIONS} />
          </div>
          {form.deadbandMode !== "0" && (
            <div className="gwField">
              <label className="gwLabel">{form.deadbandMode === "2" ? "Value (%)" : "Value"}</label>
              <NumberInput min={0} step={0.1} value={form.deadbandValue} onChange={set("deadbandValue")} placeholder="0" />
            </div>
          )}
        </div>
      )}
      <div className="gwHint">Deadband applies to numeric tags only; non-numeric tags are historized without it.</div>
    </div>
  </div>
);
